import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { type FuturesAppropriateness, getFuturesAppropriateness, postFuturesAppropriateness } from '@/services/api/profile/futures-appropriateness';

const profileFuturesAppropriatenessQueryKey = (pid: UserPid) => ['profile', 'futures-appropriateness', pid];

const profileFuturesAppropriatenessQueryOptions = (pid: UserPid) =>
  queryOptions({
    enabled: !!pid,
    queryFn: getFuturesAppropriateness,
    queryKey: profileFuturesAppropriatenessQueryKey(pid),
  });

const useProfileFuturesAppropriateness = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();
  const queryResult = useQuery(profileFuturesAppropriatenessQueryOptions(pid));

  const reset = useCallback(() => {
    queryClient.setQueryData<Partial<Extract<FuturesAppropriateness, { confirmed: false }>>>(profileFuturesAppropriatenessQueryKey(pid), { answers: {} });
  }, [pid, queryClient]);

  return {
    ...queryResult,
    reset,
  };
};

const useProfileFuturesAppropriatenessMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postFuturesAppropriateness,
    onSuccess: async (data) => {
      const queryKey = profileFuturesAppropriatenessQueryKey(pid);

      if (!data.confirmed) {
        queryClient.setQueryData(queryKey, data);
        return;
      }

      await queryClient.invalidateQueries({ queryKey });
    },
  });
};

export { useProfileFuturesAppropriateness, useProfileFuturesAppropriatenessMutation };
